<template>
  <article class="terrain-question" :data-question-id="question.questionId">
    <label v-if="isTextLike || question.type === 'number' || question.type === 'date'" class="terrain-question__label" :for="inputId">
      {{ question.label }}<span v-if="question.required" class="terrain-question__required" aria-label="obligatoria"> *</span>
    </label>
    <p v-else class="terrain-question__legend">
      {{ question.label }}<span v-if="question.required" class="terrain-question__required" aria-label="obligatoria"> *</span>
    </p>
    <p v-if="question.help" class="terrain-question__help">{{ question.help }}</p>

    <input v-if="question.type === 'shortText'" :id="inputId" class="terrain-question__control" type="text" :value="value || ''" :required="question.required" :disabled="readonly" @input="emitValue($event.target.value)">
    <textarea v-else-if="question.type === 'longText'" :id="inputId" class="terrain-question__control terrain-question__control--textarea" :value="value || ''" :required="question.required" :disabled="readonly" @input="emitValue($event.target.value)" />
    <input v-else-if="question.type === 'number'" :id="inputId" class="terrain-question__control" type="number" :value="value" :required="question.required" :disabled="readonly" @input="emitNumber($event.target.value)">
    <input v-else-if="question.type === 'date'" :id="inputId" class="terrain-question__control" type="date" :value="value || ''" :required="question.required" :disabled="readonly" @input="emitValue($event.target.value)">

    <fieldset v-else-if="question.type === 'singleSelect'" class="terrain-question__choices" :disabled="readonly">
      <legend class="sr-only">{{ question.label }}</legend>
      <label v-for="option in question.options || []" :key="option.optionId" class="terrain-question__choice">
        <input type="radio" :name="inputId" :checked="value === option.optionId" :required="question.required" @change="emitValue(option.optionId)">
        {{ option.label }}
      </label>
    </fieldset>
    <fieldset v-else-if="question.type === 'multiSelect'" class="terrain-question__choices" :disabled="readonly">
      <legend class="sr-only">{{ question.label }}</legend>
      <label v-for="option in question.options || []" :key="option.optionId" class="terrain-question__choice">
        <input type="checkbox" :checked="selectedOptions.includes(option.optionId)" @change="toggleOption(option.optionId, $event.target.checked)">
        {{ option.label }}
      </label>
    </fieldset>
    <label v-else-if="question.type === 'check'" class="terrain-question__choice">
      <input type="checkbox" :checked="value === true" :disabled="readonly" @change="emitValue($event.target.checked)">
      {{ question.label }}
    </label>

    <div v-else-if="question.type === 'table'" class="terrain-question__table-scroll" tabindex="0" :aria-label="`Tabla: ${question.label}. Deslice horizontalmente para ver todas las columnas.`">
      <table class="terrain-question__table">
        <thead><tr><th scope="col">Fila</th><th v-for="column in tableColumns" :key="column.columnId || column.id" scope="col">{{ column.label }}</th></tr></thead>
        <tbody><tr v-for="row in tableRows" :key="row.rowId || row.id"><th scope="row">{{ row.label }}</th><td v-for="column in tableColumns" :key="column.columnId || column.id"><input class="terrain-question__cell" :disabled="readonly" :value="tableValue(row, column)" @input="setTableValue(row, column, $event.target.value)"></td></tr></tbody>
      </table>
    </div>
    <div v-else-if="question.type === 'repeater'" class="terrain-question__table-scroll" tabindex="0" :aria-label="`Repetidor: ${question.label}. Deslice horizontalmente para ver todas las columnas.`">
      <table class="terrain-question__table"><thead><tr><th v-for="column in repeaterColumns" :key="column.columnId || column.id" scope="col">{{ column.label }}</th></tr></thead><tbody><tr v-for="(item, index) in repeaterItems" :key="index"><td v-for="column in repeaterColumns" :key="column.columnId || column.id"><input class="terrain-question__cell" :disabled="readonly" :value="item[column.columnId || column.id] || ''" @input="setRepeaterValue(index, column, $event.target.value)"></td></tr></tbody></table>
      <button v-if="!readonly" type="button" class="terrain-question__secondary" @click="addRepeaterItem">Agregar fila</button>
    </div>
    <p v-else-if="question.type === 'location' && readonly" class="terrain-question__readonly-placeholder">
      La ubicación se captura al responder el formulario.
    </p>
    <GpsCapture v-else-if="question.type === 'location'" mode="location" :label="question.label" :auto-capture-original="false" @captured="emitValue($event.capture)" />
    <AttachmentField
      v-else-if="question.type === 'photo' || question.type === 'file'"
      :question="question"
      :value="value"
      :campaign-id="campaignId"
      :template-version-id="templateVersionId"
      :can-upload="canUploadAttachments"
      :readonly="readonly"
      :editable="attachmentsEditable"
      @input="emitValue"
    />
    <p v-else class="terrain-question__unsupported">Tipo de pregunta no disponible: {{ question.type }}</p>
  </article>
</template>

<script>
import GpsCapture from './GpsCapture.vue';
import AttachmentField from './AttachmentField.vue';

export default {
  name: 'TerrainQuestionField',
  components: { GpsCapture, AttachmentField },
  props: {
    question: { type: Object, required: true },
    value: { default: null },
    readonly: { type: Boolean, default: false },
    campaignId: { type: String, default: null },
    templateVersionId: { type: String, default: null },
    canUploadAttachments: { type: Boolean, default: false },
    attachmentsEditable: { type: Boolean, default: false },
  },
  computed: {
    inputId() { return `terrain-question-${this.question.questionId}`; },
    isTextLike() { return ['shortText', 'longText'].includes(this.question.type); },
    selectedOptions() { return Array.isArray(this.value) ? this.value : []; },
    tableColumns() { return (this.question.table && this.question.table.columns) || []; },
    tableRows() { return (this.question.table && this.question.table.rows) || []; },
    repeaterColumns() { return (this.question.repeater && this.question.repeater.columns) || []; },
    repeaterItems() { return Array.isArray(this.value) ? this.value : []; },
  },
  methods: {
    emitValue(value) { this.$emit('input', value); },
    emitNumber(value) { this.emitValue(value === '' ? '' : Number(value)); },
    toggleOption(optionId, checked) { const next = new Set(this.selectedOptions); checked ? next.add(optionId) : next.delete(optionId); this.emitValue(Array.from(next)); },
    tableValue(row, column) { const table = this.value || {}; return (table[row.rowId || row.id] || {})[column.columnId || column.id] || ''; },
    setTableValue(row, column, value) { const rowId = row.rowId || row.id; const columnId = column.columnId || column.id; const table = { ...(this.value || {}) }; table[rowId] = { ...(table[rowId] || {}), [columnId]: value }; this.emitValue(table); },
    setRepeaterValue(index, column, value) { const columnId = column.columnId || column.id; const items = this.repeaterItems.map((item) => ({ ...item })); items[index] = { ...items[index], [columnId]: value }; this.emitValue(items); },
    addRepeaterItem() { this.emitValue([...this.repeaterItems, {}]); },
  },
};
</script>

<style scoped lang="scss">
.terrain-question {
  min-width: 0;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-md);
  display: grid;
  gap: var(--terrain-space-sm);
}

.terrain-question__label, .terrain-question__legend { color: var(--color-navy); font-weight: 700; margin: 0; }
.terrain-question__required { color: var(--color-danger); }
.terrain-question__help { color: var(--color-ghost); font-size: .9rem; margin: 0; }

.terrain-question__control, .terrain-question__cell {
  box-sizing: border-box;
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  max-width: 100%;
  padding: var(--terrain-space-sm);
  width: 100%;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

.terrain-question__control--textarea { min-height: 100px; resize: vertical; }
.terrain-question__choices { border: 0; display: grid; gap: var(--terrain-space-xs); margin: 0; padding: 0; }
.terrain-question__choice { display: flex; gap: var(--terrain-space-sm); align-items: center; }
.terrain-question__table-scroll { border: 1px solid var(--color-line); border-radius: var(--terrain-radius-md); max-width: 100%; overflow-x: auto; }
.terrain-question__table { border-collapse: collapse; min-width: 540px; width: 100%; }
.terrain-question__table th, .terrain-question__table td { border: 1px solid var(--color-line); padding: var(--terrain-space-xs) var(--terrain-space-sm); text-align: left; white-space: nowrap; }
.terrain-question__table th { background: var(--color-mist); color: var(--color-navy); }
.terrain-question__cell { min-width: 120px; }

.terrain-question__secondary {
  margin: var(--terrain-space-sm) 0 0;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-navy);
  cursor: pointer;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover { background: var(--color-mist); border-color: var(--color-steel); }
}

.terrain-question__unsupported { color: var(--color-danger); margin: 0; }
.terrain-question__readonly-placeholder {
  background: var(--color-mist);
  border: 1px dashed var(--color-line);
  border-radius: var(--terrain-radius-md);
  color: var(--color-ghost);
  margin: 0;
  padding: var(--terrain-space-sm);
}
.sr-only { height: 1px; overflow: hidden; position: absolute; width: 1px; }
</style>
